/**
 * recordVisibility — frontend mirror of the canonical CRM visibility predicate.
 *
 * THE single place the frontend decides "which contacts/deals can this user
 * see". Parity contract (keep all three in sync):
 *   - SQL:     crm_user_can_access() / crm_accessible_owner_ids()
 *              (supabase/migrations/20261101_01_multi_owner_foundation.sql)
 *   - Backend: functions/CRM/utils/recordVisibility.js
 *   - Here:    src/utils/recordVisibility.js
 *
 * The model (Phase 0 of the multi-owner CRM):
 *   A user U can see record R iff any of:
 *     1. U is a CRM admin                      (admin override — unchanged)
 *     2. R.owner_id === U                      (primary owner — unchanged)
 *     3. R.owner_id is in U's accessible owners (book share: a crm_teams owner
 *        granted U their whole book via crm_team_members)
 *     4. U ∈ R.shared_with_ids                 (record-level grant: manual
 *        share, deal-team membership, or company allowlist —
 *        crm_record_shares mirror)
 *     5. R.record_scope === 'company' AND U is a CRM admin
 *        (company records live in every admin's own book)
 *
 * Scope object shape (produced by buildVisibilityScope / useVisibilityScope):
 *   { mode: 'all' }                            → no filter (admin "All Users")
 *   { mode: 'scoped', targetUid, ownerIds, includeCompany }
 *     targetUid      uid whose effective book is being viewed
 *     ownerIds       accessible owner uids (always includes targetUid)
 *     includeCompany true when the target is a CRM admin → company records
 *                    appear in their own book
 */

import { getSupabase, isSupabaseConfigured } from './supabase';

export const VISIBILITY_ENTITY_TYPES = Object.freeze({
  CONTACT: 'contact',
  DEAL: 'deal',
});

/**
 * Quote a value for use inside a PostgREST `in.(...)` list.
 * Firebase UIDs are alphanumeric, but quoting defends against commas/spaces.
 */
function quoteInListValue(value) {
  return `"${String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

/**
 * Quote one element of a Postgres array literal for `cs.{...}`.
 */
function quoteArrayElement(value) {
  return `"${String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

/**
 * Normalize a contact/deal row (snake_case from Supabase or camelCase after
 * transform) into the three visibility fields.
 */
export function normalizeVisibilityFields(record) {
  if (!record) return { ownerId: null, sharedWithIds: [], recordScope: 'user' };
  return {
    ownerId: record.owner_id !== undefined ? record.owner_id : record.ownerId ?? null,
    sharedWithIds:
      (record.shared_with_ids !== undefined ? record.shared_with_ids : record.sharedWithIds) || [],
    recordScope:
      (record.record_scope !== undefined ? record.record_scope : record.recordScope) || 'user',
  };
}

/**
 * Pure predicate: can `uid` see this record?
 * Mirrors SQL crm_user_can_access(). Used by detail pages and tests.
 *
 * @param {Object} params
 * @param {string} params.uid              viewing user's Firebase UID
 * @param {boolean} params.isAdmin         viewer is a CRM admin
 * @param {Object} params.record           row with owner/shared/scope fields (snake or camel)
 * @param {string[]} [params.accessibleOwnerIds] uids whose books the viewer can
 *   see (from fetchAccessibleOwnerIds). Defaults to just [uid].
 * @returns {boolean}
 */
export function canUserSeeRecord({ uid, isAdmin = false, record, accessibleOwnerIds = null }) {
  if (!uid || !record) return false;
  if (isAdmin) return true;

  const { ownerId, sharedWithIds, recordScope } = normalizeVisibilityFields(record);
  if (ownerId === uid) return true;
  if (Array.isArray(sharedWithIds) && sharedWithIds.includes(uid)) return true;

  const owners = Array.isArray(accessibleOwnerIds) && accessibleOwnerIds.length > 0
    ? accessibleOwnerIds
    : [uid];
  if (ownerId && owners.includes(ownerId)) return true;

  // record_scope='company' without admin rights only grants access through the
  // allowlist (shared_with_ids), which was already checked above.
  void recordScope;
  return false;
}

/**
 * Build the PostgREST `.or(...)` argument for a scoped visibility filter.
 * Exported for tests and for call sites that need the raw string.
 *
 * @param {Object} scope { targetUid, ownerIds, includeCompany }
 * @returns {string} e.g. `owner_id.in.("a","b"),shared_with_ids.cs.{"a"},record_scope.eq.company`
 */
export function buildVisibilityOrFilter(scope) {
  const targetUid = scope?.targetUid;
  const ownerIds = Array.isArray(scope?.ownerIds) && scope.ownerIds.length > 0
    ? scope.ownerIds
    : targetUid
      ? [targetUid]
      : [];

  const clauses = [];
  if (ownerIds.length > 0) {
    clauses.push(`owner_id.in.(${ownerIds.map(quoteInListValue).join(',')})`);
  }
  if (targetUid) {
    clauses.push(`shared_with_ids.cs.{${quoteArrayElement(targetUid)}}`);
  }
  if (scope?.includeCompany) {
    clauses.push('record_scope.eq.company');
  }
  return clauses.join(',');
}

/**
 * Apply a visibility scope to a Supabase query builder.
 *
 * - null/undefined scope or { mode: 'all' } → query unchanged (admin All Users)
 * - { mode: 'scoped', ... } → OR of: owned by an accessible owner, record-level
 *   shared with the target, or (admins) company-scoped.
 *
 * For an unshared loan officer this produces
 *   .or('owner_id.in.("uid"),shared_with_ids.cs.{"uid"}')
 * which returns exactly the same rows as the old .eq('owner_id', uid).
 *
 * @param {Object} query  supabase query builder
 * @param {Object|null} scope visibility scope
 * @returns {Object} query
 */
export function applyVisibilityScope(query, scope) {
  if (!scope || scope.mode === 'all') return query;
  const orFilter = buildVisibilityOrFilter(scope);
  if (!orFilter) return query;
  return query.or(orFilter);
}

/**
 * Build a scope object synchronously (no team expansion yet).
 * Used as the immediate value while fetchAccessibleOwnerIds resolves, so the
 * first render is never wider than the old single-owner behavior.
 *
 * @param {Object} params
 * @param {string|null} params.targetUserId uid whose book is viewed; null +
 *   viewerIsAdmin = All Users
 * @param {boolean} params.viewerIsAdmin
 * @param {string} params.viewerUid
 * @param {boolean} [params.targetIsAdmin] whether the target user is a CRM
 *   admin (controls the company-record clause). Defaults to viewerIsAdmin when
 *   the target is the viewer.
 * @returns {Object} scope
 */
export function buildVisibilityScope({
  targetUserId,
  viewerIsAdmin = false,
  viewerUid,
  targetIsAdmin = undefined,
  ownerIds = null,
}) {
  if (viewerIsAdmin && !targetUserId) {
    return { mode: 'all' };
  }
  const targetUid = targetUserId || viewerUid;
  if (!targetUid) {
    // No identity at all — match nothing rather than everything.
    return { mode: 'scoped', targetUid: '__none__', ownerIds: ['__none__'], includeCompany: false };
  }
  const resolvedTargetIsAdmin =
    targetIsAdmin !== undefined
      ? targetIsAdmin
      : targetUid === viewerUid
        ? viewerIsAdmin
        : false;
  return {
    mode: 'scoped',
    targetUid,
    ownerIds: Array.isArray(ownerIds) && ownerIds.length > 0 ? ownerIds : [targetUid],
    includeCompany: !!resolvedTargetIsAdmin,
  };
}

/**
 * Fetch the accessible owner uids for a user via the canonical SQL function
 * (single round trip; guaranteed parity with the database predicate).
 *
 * @param {string} uid
 * @param {'contact'|'deal'} entityType
 * @returns {Promise<string[]>} always includes uid; falls back to [uid] on error
 */
export async function fetchAccessibleOwnerIds(uid, entityType) {
  if (!uid) return [];
  if (!isSupabaseConfigured()) return [uid];
  try {
    const supabase = getSupabase();
    const { data, error } = await supabase.rpc('crm_accessible_owner_ids', {
      p_uid: uid,
      p_entity_type: entityType,
    });
    if (error) throw error;
    const owners = Array.isArray(data) ? data.filter(Boolean) : [];
    return owners.includes(uid) ? owners : [uid, ...owners];
  } catch (err) {
    console.error('[recordVisibility] fetchAccessibleOwnerIds failed:', err);
    return [uid];
  }
}

/**
 * Stable string key for a scope — use in React deps / memoization.
 */
export function visibilityScopeKey(scope) {
  if (!scope) return 'none';
  if (scope.mode === 'all') return 'all';
  const owners = [...(scope.ownerIds || [])].sort().join('|');
  return `scoped:${scope.targetUid}:${owners}:${scope.includeCompany ? 'co' : 'no'}`;
}

const recordVisibility = {
  VISIBILITY_ENTITY_TYPES,
  normalizeVisibilityFields,
  canUserSeeRecord,
  buildVisibilityOrFilter,
  applyVisibilityScope,
  buildVisibilityScope,
  fetchAccessibleOwnerIds,
  visibilityScopeKey,
};

export default recordVisibility;
